import { useState } from "react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { cn } from "@/lib/utils";
import { ChevronDown } from "lucide-react";

const faqs = [
  {
    q: "What are State Snacks?",
    a: "State Snacks are functional snacks designed to support how you want to feel — whether that's calm, focused, energised, or ready for sleep. Each pouch is crafted with purposeful, science-backed ingredients that taste great and actually do something.",
  },
  {
    q: "What's in them?",
    a: "Real food ingredients combined with functional botanicals and adaptogens. Think tart cherry and magnesium for Sleep, L-theanine and lavender for Chill, B-vitamins and green tea caffeine for Energy, and maca root with green tea for Focus. No artificial junk, ever.",
  },
  {
    q: "Are they safe?",
    a: "Absolutely. All ingredients are food-grade, well-researched, and used at effective but safe levels. They're not supplements — they're snacks made with functional ingredients you could find in nature. That said, always check the label if you have specific allergies.",
  },
  {
    q: "When do you launch?",
    a: "We're in the final stages of development. Join the waitlist to be first in line — early supporters will get exclusive access and launch-day pricing.",
  },
  {
    q: "How much will they cost?",
    a: "We're keeping pricing accessible. Exact prices will be announced at launch, but think premium-snack pricing — not supplement pricing. Waitlist members will get the best deal.",
  },
  {
    q: "Can I try before I buy?",
    a: "We're planning a limited sample drop for early waitlist members before the full launch. Sign up and you'll be the first to know when samples are available.",
  },
];

const FAQ = () => {
  const { ref, isVisible } = useScrollAnimation();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (i: number) => setOpenIndex(openIndex === i ? null : i);

  return (
    <section id="faq" className="py-24 md:py-32 bg-[hsl(var(--pastel-pink))]">
      <div ref={ref} className="mx-auto max-w-5xl px-6">
        <div
          className={cn(
            "mb-12 text-center transition-all duration-700",
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          )}
        >
          <h2 className="heading-bold text-4xl text-foreground sm:text-5xl lg:text-[4rem] lg:leading-[0.95]">
            Frequently asked questions
          </h2>
        </div>

        <div
          className={cn(
            "grid grid-cols-1 md:grid-cols-2 gap-4 transition-all duration-700 ease-out",
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          )}
          style={{ transitionDelay: "200ms" }}
        >
          {faqs.map((faq, i) => (
            <button
              key={i}
              onClick={() => toggle(i)}
              className="flex flex-col items-start rounded-2xl bg-background/60 px-6 py-5 text-left transition-all hover:bg-background/80"
            >
              <div className="flex w-full items-center justify-between gap-4">
                <span className="text-base font-bold text-foreground sm:text-lg">
                  {faq.q}
                </span>
                <ChevronDown
                  className={cn(
                    "h-5 w-5 shrink-0 text-foreground/50 transition-transform duration-300",
                    openIndex === i && "rotate-180"
                  )}
                />
              </div>
              <div
                className={cn(
                  "grid transition-all duration-300 ease-out",
                  openIndex === i ? "grid-rows-[1fr] mt-3 opacity-100" : "grid-rows-[0fr] opacity-0"
                )}
              >
                <div className="overflow-hidden">
                  <p className="text-sm leading-relaxed text-foreground/60 sm:text-base">
                    {faq.a}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQ;
